import { useEffect, useRef, useState } from 'react';
import { CALM_CONFIG as CC } from '@/show/saysConfig';

const rand = (a, b) => a + Math.random() * (b - a);
let nextId = 1;

// a glowing mycelium tendril sprouting from a screen edge or a random point, curving inwards
function makeTendril(w, h) {
  const edge = Math.random() < 0.6;
  let x0;
  let y0;
  if (edge) {
    const side = Math.floor(Math.random() * 4);
    x0 = side === 0 ? 0 : side === 1 ? w : rand(0.08, 0.92) * w;
    y0 = side === 2 ? 0 : side === 3 ? h : rand(0.12, 0.88) * h;
  } else {
    x0 = rand(0.12, 0.88) * w;
    y0 = rand(0.18, 0.8) * h;
  }
  const toC = Math.atan2(h / 2 - y0, w / 2 - x0) + rand(-0.9, 0.9);
  const len = rand(170, 330);
  const bend = rand(-1, 1) * len * 0.45;
  const px = -Math.sin(toC);
  const py = Math.cos(toC);
  const at = (f, b) => [x0 + Math.cos(toC) * len * f + px * bend * b, y0 + Math.sin(toC) * len * f + py * bend * b];
  const [c1x, c1y] = at(0.35, 0.9);
  const [c2x, c2y] = at(0.7, -0.6);
  const [x1, y1] = at(1, 0.2);
  return { id: nextId++, x0, y0, x1, y1, d: `M${x0},${y0} C${c1x},${c1y} ${c2x},${c2y} ${x1},${y1}`, life: rand(CC.lifeMs[0], CC.lifeMs[1]) };
}

const Tendril = ({ t, color, onTap, onGone }) => {
  const [state, setState] = useState('grow');
  useEffect(() => {
    const id = setTimeout(() => setState((s) => (s === 'grow' ? 'retract' : s)), t.life);
    return () => clearTimeout(id);
  }, [t.life]);
  useEffect(() => {
    if (state === 'grow') return undefined;
    const id = setTimeout(() => onGone(t.id), state === 'curl' ? 900 : 550);
    return () => clearTimeout(id);
  }, [state, onGone, t.id]);
  const hit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (state !== 'grow') return;
    setState('curl');
    onTap(t);
  };
  return (
    <g data-testid={`calm-tendril-${t.id}`} data-state={state} className={`tendril tendril-${state}`} style={{ transformOrigin: `${t.x0}px ${t.y0}px`, '--tc': color }} onPointerDown={hit}>
      <path d={t.d} pathLength="1" className="tendril-glow" />
      <path d={t.d} pathLength="1" className="tendril-core" />
      <circle cx={t.x1} cy={t.y1} r="9" className="tendril-tip" />
      <path d={t.d} className="tendril-hit" />
      <circle cx={t.x1} cy={t.y1} r="46" className="tendril-hit-tip" />
      {state === 'curl' && Array.from({ length: 10 }, (_, i) => {
        const a = (i / 10) * Math.PI * 2 + rand(-0.2, 0.2);
        return <circle key={i} cx={t.x1} cy={t.y1} r={rand(2, 5)} className="spore-burst" style={{ '--bx': `${Math.cos(a) * rand(40, 90)}px`, '--by': `${Math.sin(a) * rand(40, 90)}px` }} />;
      })}
    </g>
  );
};

function useTendrils(playing, calmPct) {
  const [list, setList] = useState([]);
  const calmRef = useRef(calmPct);
  calmRef.current = calmPct;
  useEffect(() => {
    if (!playing) return undefined;
    let timer;
    const spawn = () => {
      setList((l) => (l.length >= CC.maxLive + 3 ? l : [...l, makeTendril(window.innerWidth, window.innerHeight)]));
      const f = calmRef.current / 100;
      timer = setTimeout(spawn, (CC.spawnMs[0] + (CC.spawnMs[1] - CC.spawnMs[0]) * f) * rand(0.7, 1.2));
    };
    spawn();
    return () => clearTimeout(timer);
  }, [playing]);
  useEffect(() => {
    if (!playing) setList([]);
  }, [playing]);
  const remove = useRef((id) => setList((l) => l.filter((x) => x.id !== id))).current;
  return [list, remove];
}

const CalmMeter = ({ g }) => (
  <div data-testid="calm-meter" className="pointer-events-none absolute inset-x-0 bottom-10 mx-auto w-[min(1100px,80vw)]">
    <div className="flex items-end justify-between">
      <div className="font-display text-5xl font-black tracking-[0.12em] glow-spore">CALM</div>
      <div data-testid="calm-value" className="font-display text-5xl font-black">{g.calm}%</div>
    </div>
    <div className="mt-2 h-6 border border-white/20 bg-black/40">
      <div data-testid="calm-bar" className="h-full transition-[width] duration-300" style={{ width: `${g.calm}%`, background: 'var(--spore)', boxShadow: '0 0 30px var(--spore)' }} />
    </div>
  </div>
);

export const CalmGame = ({ snap, onSoothe, onYes }) => {
  const g = snap.calmGame;
  const [list, remove] = useTendrils(g.stage === 'play', g.calm);
  const hue = 350 + (g.calm / 100) * 160; // agitated red → spore green
  const color = `hsl(${hue % 360} 100% 60%)`;
  return (
    <div data-testid="calm-game" data-stage={g.stage} className="absolute inset-0 z-30 select-none" style={{ touchAction: 'none' }}>
      <div className={`pointer-events-none absolute inset-0 ${g.stage === 'done' ? 'calm-vignette-done' : 'calm-vignette'}`} style={{ '--agit': (100 - g.calm) / 100 }} />
      <div className="pointer-events-none absolute left-10 top-10">
        <div className="label-eyebrow">SPORE LAB · CONTAINMENT PROTOCOL · [ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</div>
        <div data-testid="calm-agitation" className="font-display mt-2 text-3xl font-black tracking-[0.08em]" style={{ color: g.stage === 'done' ? 'var(--spore)' : '#ff5a6e' }}>
          SPECIMEN AGITATION: {g.stage === 'done' ? 'NOMINAL' : `${100 - g.calm}%`}
        </div>
      </div>
      {g.stage === 'upset' && (
        <div data-testid="calm-upset" className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="calm-alarm font-display text-7xl font-black tracking-[0.1em] text-[#ff5a6e] lg:text-9xl">SPECIMEN UPSET</div>
        </div>
      )}
      {g.stage === 'play' && (
        <svg data-testid="calm-field" className="absolute inset-0 h-full w-full" width="100%" height="100%">
          {list.map((t) => <Tendril key={t.id} t={t} color={color} onTap={onSoothe} onGone={remove} />)}
        </svg>
      )}
      {g.stage === 'play' && <div className="pointer-events-none absolute inset-x-0 top-[12%] text-center label-eyebrow text-lg">TAP ZE TENDRILS · SOOTHE 'ER</div>}
      {g.stage === 'done' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-10">
          <div data-testid="calm-done" className="font-display text-6xl font-black tracking-[0.1em] glow-spore lg:text-8xl">SHE IS CALM</div>
          <div className="max-w-[900px] text-center text-2xl text-[var(--ink)]">Want to return to see if she forgives you now, and will let you stroke &apos;er?</div>
          <button type="button" data-testid="calm-yes-button" onClick={onYes} className="landing-btn border-2 border-[var(--spore)] bg-[var(--panel-2)]/80 px-24 py-10 font-display text-7xl font-black transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-[#10b98122] active:scale-95">
            <span className="glow-spore">YES</span>
          </button>
        </div>
      )}
      {g.stage !== 'done' && <CalmMeter g={g} />}
    </div>
  );
};
